import { useMemo, useCallback } from "react";
import { useConnection, useWallet } from "@solana/wallet-adapter-react";
import { Connection, PublicKey, LAMPORTS_PER_SOL } from "@solana/web3.js";
import {
  TOKEN_PROGRAM_ID,
  TOKEN_2022_PROGRAM_ID,
  getAssociatedTokenAddressSync,
} from "@solana/spl-token";
import useSWR, { Fetcher } from 'swr';
import { TOKENS } from "~/constants";
import idlJson from "../programs/dag.json";

// Program ID from the DAG program
const PROGRAM_ID = new PublicKey(idlJson.address);

// --- Types ---
interface Balances {
  [mintAddress: string]: number;
}
type BalancesKey = readonly ['userBalances', string] | null;
type DevPoolBalanceKey = readonly ['devPoolBalance', string] | null;

const SOL = 'SOL';

// --- Helper: Create a set of known mint addresses (outside hook) ---
const createKnownMintAddressSet = (): Set<string> => {
  const mints = new Set<string>();
  Object.values(TOKENS).forEach(token => {
    mints.add(token.address);
  });
  return mints;
};

const knownMintAddresses = createKnownMintAddressSet(); // Calculate once globally

// --- Helper: Get Project PDA ---
const getProjectPda = (tokenAddress: string) => {
  const tokenMintPublicKey = new PublicKey(tokenAddress);
  const [pda] = PublicKey.findProgramAddressSync(
    [Buffer.from("project"), tokenMintPublicKey.toBuffer()],
    PROGRAM_ID
  );
  return pda;
};

// --- Helper: Get Token Vault Address (Dev Pool) ---
const getTokenVaultAddress = (tokenAddress: string, tokenProgram: PublicKey) => {
  const tokenMintPublicKey = new PublicKey(tokenAddress);
  const projectPda = getProjectPda(tokenAddress);
  
  return getAssociatedTokenAddressSync(
    tokenMintPublicKey,
    projectPda,
    true, // allowOwnerOffCurve
    tokenProgram
  );
};

// --- SWR Fetcher for User Balances (outside hook) ---
const fetchUserBalances = async (
    key: BalancesKey,
    connection: Connection | null,
): Promise<Balances> => {
    if (!key) throw new Error("SWR key is null, skipping fetch");
    if (!connection) throw new Error("Solana connection not available");

    const [, userAddress] = key;
    const userPublicKey = new PublicKey(userAddress);

    try {
        // Get native SOL balance and all token accounts
        const [nativeBalanceLamports, tokenAccounts, token2022Accounts] = await Promise.all([
            connection.getBalance(userPublicKey, 'confirmed'),
            connection.getParsedTokenAccountsByOwner(userPublicKey, { programId: TOKEN_PROGRAM_ID }, 'confirmed'),
            connection.getParsedTokenAccountsByOwner(userPublicKey, { programId: TOKEN_2022_PROGRAM_ID }, 'confirmed')
        ]);

        // Process native SOL balance
        const nativeSolBalance = nativeBalanceLamports / LAMPORTS_PER_SOL;

        // Initialize balances object
        const splBalances: Balances = {};

        // Combine all token accounts
        const allAccounts = [...tokenAccounts.value, ...token2022Accounts.value];

        // Process all token accounts, not just known ones
        allAccounts.forEach(accountInfo => {
            if (accountInfo?.account?.data?.parsed?.info) {
                const info = accountInfo.account.data.parsed.info;
                const mintAddress = info.mint;
                splBalances[mintAddress] = info.tokenAmount?.uiAmount ?? 0;
            }
        });

        // Combine SOL and token balances
        const finalBalances: Balances = {
            [SOL]: nativeSolBalance,
            ...splBalances
        };

        return finalBalances;

    } catch (error) {
        console.error("Failed to fetch balances:", error);
        throw error;
    }
};

// --- SWR Fetcher for Dev Pool Balance (outside hook) ---
const fetchDevPoolBalance = async (
    key: DevPoolBalanceKey,
    connection: Connection | null,
): Promise<number> => {
    if (!key) throw new Error("SWR key is null, skipping fetch");
    if (!connection) throw new Error("Solana connection not available");

    const [, tokenAddress] = key;
    console.log("Fetcher: Fetching dev pool balance for token", tokenAddress);

    try {
        // Get the token vault address (dev pool) using TOKEN_2022_PROGRAM_ID
        const tokenVaultAddress = getTokenVaultAddress(tokenAddress, TOKEN_2022_PROGRAM_ID);
        
        // Fetch the token account balance
        const tokenAccountBalance = await connection.getTokenAccountBalance(tokenVaultAddress, 'confirmed');
        
        const balance = tokenAccountBalance.value.uiAmount ?? 0;
        console.log("Fetcher: Dev pool balance:", balance);
        
        return balance;
    } catch (error) {
        console.error("Fetcher: Failed to fetch dev pool balance:", error);
        throw error;
    }
};

// --- useBalance Hook ---
export const useBalance = () => {
  const { connection } = useConnection();
  const { publicKey } = useWallet();

  const swrKey = useMemo<BalancesKey>(() => 
    publicKey ? ['userBalances', publicKey.toBase58()] : null, 
    [publicKey]
  );

  const { data: balances, error, isLoading, mutate } = useSWR(
    swrKey,
    (key) => fetchUserBalances(key, connection),
    {
      revalidateOnFocus: true,
      revalidateOnReconnect: true
    }
  );

  const getBalance = useCallback((address: string = SOL): number => {
    // If no address or no balances data, return 0
    if (!address || !balances) return 0;
    // Return the balance if it exists, otherwise 0
    return balances[address] ?? 0;
  }, [balances]);

  const getAllBalances = useCallback((): Balances | null => {
    return balances ?? null;
  }, [balances]);

  // Dev Pool Balance Hook
  const getDevPoolBalance = useCallback((tokenAddress: string) => {
    const devPoolKey = useMemo<DevPoolBalanceKey>(() => tokenAddress ? ['devPoolBalance', tokenAddress] : null, [tokenAddress]);
    
    const { data: devPoolBalance, error: devPoolError, isLoading: devPoolLoading, mutate: mutateDevPool } = useSWR(
      devPoolKey,
      (key) => fetchDevPoolBalance(key, connection),
      {
        refreshInterval: 10000, // Refresh every 10 seconds
      }
    );

    return {
      balance: devPoolBalance ?? 0,
      error: devPoolError,
      isLoading: devPoolLoading,
      mutate: mutateDevPool,
    };
  }, [connection]);

  return {
    getBalance,
    getAllBalances,
    getDevPoolBalance,
    isLoading,
    error,
    mutate
  };
}

// Export helper functions for use in other hooks/components
export { getProjectPda, getTokenVaultAddress };

